import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  TActionTypeToTotalMap,
  TDefectiveFromServer,
  TREQUEST_ServerDefectives,
  TServerDefectivesStore,
} from '@monorepo/types';
import { getAllStationIds, getSubjectKey, selectAppInfo, selectedSubject } from '../app-master-db/app-master.query';
import { BCTXComputationHelper, EventUtil } from '@monorepo/utils';
import {
  getAllBctxActualsMap,
  getAllBctxActualsMapPerPiece,
  getEventsArray,
  getPastDaysDefectivesInfo,
  getselectedBctxInfo,
  getSelectedStationIdAndShiftId,
} from '../input-app-client-db/input-app-client-db.query';
import { act } from '@ngrx/effects';

export const selectServerDefectivesStore = createFeatureSelector<TServerDefectivesStore>('SERVER_DEFECTIVES_STORE');
export const getLockDetailsAndNextCacheResetTime = createSelector(
  selectServerDefectivesStore,
  (serverDefectivesStore) => {
    return {
      lockDetails: serverDefectivesStore.lockDetails,
      nextCacheResetTimestamp: serverDefectivesStore.serverDefectivesDB.dailyCache.nextCacheResetTimestamp,
    };
  },
);

export const getServerDefectivesPayload = createSelector(
  selectAppInfo,
  selectedSubject,
  getSubjectKey,
  getAllStationIds,
  (app, selectedSubject, subjecKey, getAllStationIds) => {
    const payload: TREQUEST_ServerDefectives = {
      date: BCTXComputationHelper.dashFormateDate(),
      app_type: app ? app.appInfo.product_type : 'no_app_type',
      hsk: selectedSubject ? selectedSubject.hsk : '',
      subject_key: subjecKey,
      refactor: true,
      domerole: selectedSubject ? selectedSubject.gatek : '',
      sub_id: getAllStationIds ? getAllStationIds : [],
    };
    return payload;
  },
);

export const getAllServerDefectives = createSelector(selectServerDefectivesStore, (store) => {
  return store.serverDefectivesDB.dailyCache.lineWiseServerDefectives;
});

export const hasServerDefectivesInitialized = createSelector(selectServerDefectivesStore, (store) => {
  return store.serverDefectivesDB.dailyCache.hasInitialized;
});

export const latestServerDefectivesSyncTime = createSelector(selectServerDefectivesStore, (store) => {
  return store.serverDefectivesDB.dailyCache.lastSyncTime;
});

export const getMultipleRoundsDefectivesForCurrentLineAndShiftAndSelectedBctx = createSelector(
  getEventsArray,
  selectServerDefectivesStore,
  getselectedBctxInfo,
  getSelectedStationIdAndShiftId,
  getPastDaysDefectivesInfo,
  (events, serverDefectivesStore, getselectedBctxItem, getSelectedStationIdAndShiftId, pastDaysDefectives) => {
    const { shiftId, stationId } = getSelectedStationIdAndShiftId;
    return EventUtil.getDefectivesFromEventsArray(
      shiftId,
      stationId,
      events,
      serverDefectivesStore.serverDefectivesDB.dailyCache.lineWiseServerDefectives,
      pastDaysDefectives,
      getselectedBctxItem.bctxInfoWithoutSize.list.length === 0
        ? null
        : getselectedBctxItem.bctxInfoWithoutSize.bctxKey,
    );
  },
);

export const getMultipleRoundsDefectivesForCurrentLineAndShiftAndSelectedBctxAndInitStatus = createSelector(
  getMultipleRoundsDefectivesForCurrentLineAndShiftAndSelectedBctx,
  hasServerDefectivesInitialized,
  (defects, hasInitialized) => {
    return {
      hasInitialized: hasInitialized,
      defects,
    };
  },
);

export const getMultipleRoundsDefectivesForCurrentLineAndShift = createSelector(
  getEventsArray,
  selectServerDefectivesStore,
  getSelectedStationIdAndShiftId,
  getPastDaysDefectivesInfo,
  (events, serverDefectivesStore, getSelectedStationIdAndShiftId, pastDaysDefectives) => {
    const { shiftId, stationId } = getSelectedStationIdAndShiftId;
    return EventUtil.getDefectivesFromEventsArray(
      shiftId,
      stationId,
      events,
      serverDefectivesStore.serverDefectivesDB.dailyCache.lineWiseServerDefectives,
      pastDaysDefectives,
      null,
    );
  },
);

export const getPerPieceDefectivesForAllSubjects = createSelector(
  getEventsArray,
  selectServerDefectivesStore,
  (events, serverDefectivesStore) => {
    return EventUtil.getPerPieceDefectivesFromEventsArray(events, serverDefectivesStore.serverDefectivesDB.dailyCache);
  },
);

export const getPerPieceDefectivesForCurrentLine = createSelector(
  getPerPieceDefectivesForAllSubjects,
  getSelectedStationIdAndShiftId,
  (defectives, { stationId }) => {
    return defectives.filter((defective) => {
      if (defective.eventInfo.type === 'USER_EVENT')
        return defective.eventInfo.event.metadata.stationDetails.subject.subject === stationId;
      else return defective.eventInfo.event.eventInfo.line === stationId;
    });
  },
);

export const getAllActualsForAllBctx = createSelector(
  getPerPieceDefectivesForCurrentLine,
  getAllBctxActualsMapPerPiece,
  (defectives, bctxActualsMap) => {
    const actuals: TActionTypeToTotalMap = {
      ...bctxActualsMap,
      TO_BE_RECTIFIED: defectives.length,
      IN_PROGRESS: defectives.filter((defective) => {
        if (defective.eventInfo.type === 'USER_EVENT')
          return BCTXComputationHelper.isToday(defective.eventInfo.event.events[0].eventInfo.eventTime);
        else return BCTXComputationHelper.isToday(defective.eventInfo.event.eventInfo.eventTime);
      }).length,
    };
    return actuals;
  },
);

export const getPerPieceDefectivesForAllSubjectsAndInitStatus = createSelector(
  getPerPieceDefectivesForAllSubjects,
  hasServerDefectivesInitialized,
  (defectives, hasInitialized) => {
    return {
      defectives,
      hasInitialized: hasInitialized,
    };
  },
);

export const getServerDefectivesForAllSubjects = createSelector(
  getEventsArray,
  selectServerDefectivesStore,
  getPastDaysDefectivesInfo,
  (events, serverDefectivesStore, pastDaysDefectives) => {
    const serverDefectives: TDefectiveFromServer[] = [];
    Object.entries(serverDefectivesStore.serverDefectivesDB.dailyCache.lineWiseServerDefectives).forEach(
      ([stationId, shiftWiseServerDefectives]) => {
        Object.keys(shiftWiseServerDefectives.shiftWiseServerDefectives).forEach((shiftId) => {
          serverDefectives.push(
            ...EventUtil.getDefectivesFromEventsArray(
              shiftId,
              stationId,
              events,
              serverDefectivesStore.serverDefectivesDB.dailyCache.lineWiseServerDefectives,
              pastDaysDefectives,
              null,
            )
              .filter((defective) => defective.event.actionType === 'SERVER_DEFECTIVE')
              .map((defective) => defective.event as TDefectiveFromServer),
          );
        });
      },
    );
    return serverDefectives;
  },
);

export const getPerPieceServerDefectivesForAllSubjects = createSelector(
  getPerPieceDefectivesForAllSubjects,
  (defectives) => {
    return defectives
      .filter((defective) => defective.eventInfo.type === 'SERVER_DEFECTIVE')
      .map((defective) => defective.eventInfo.event as TDefectiveFromServer);
  },
);
